import dayjs, { type Dayjs } from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getReservationReports } from '../../../api/endpoints/reports';
import type { BookingStatus } from '../../../api/types/common';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ReservationIdentifierLink } from '../../../components/ReservationIdentifierLink';
import { isPaymentResumable, resumePaymentUrl } from '../paymentResume';

const filterSchema = z
  .object({
    startDate: z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
      message: 'Başlangıç tarihi seçmelisiniz.',
    }),
    endDate: z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
      message: 'Bitiş tarihi seçmelisiniz.',
    }),
  })
  .refine((data) => !data.endDate.isBefore(data.startDate, 'day'), {
    message: 'Bitiş tarihi başlangıç tarihinden önce olamaz.',
    path: ['endDate'],
  });

type FilterValues = z.infer<typeof filterSchema>;

/**
 * Shared Air reservation-report list — thin filter form over the shared
 * `Report/Reservations` endpoint (see api/endpoints/reports.ts / api/types/reports.ts),
 * fixed to `providerType: 'Air'`. Rendered as a plain MUI Table (no @mui/x-data-grid —
 * incompatible with this project's pinned TypeScript 4.9.5). Row shape is fully
 * confirmed from real Air data (see api/types/reports.ts header) — 903 real rows were
 * observed for `{providerType:'Air', status:'Booked'}` in the local dev DB while
 * confirming that endpoint.
 *
 * `status` is a fixed prop, not a user-facing filter — this backs both "Rezervasyon
 * Listesi" (`status="Prebooked"`, reservations in progress that haven't been ticketed
 * yet) and "Bilet Listesi" (`status="Booked"`, actually issued tickets), the two Air
 * sub-menu entries under 'Uçak' (see ../index.tsx and pages/AirReservationsPage.tsx /
 * pages/AirTicketsPage.tsx).
 *
 * No inline row actions — the PNR (or ticket number, when a row has no PNR) is a link
 * into `ReservationDetailPage` (see ../../../components/ReservationDetailPage.tsx), which
 * re-fetches that one record fresh via `Entry/Find` and, per its status, hosts the
 * reservation actions (İptal Et / Opsiyon-Fiyat) or the ticket actions (Void / İade).
 *
 * `showTicketColumn` adds a "Bilet No" column (`row.documentNumber`) — only passed by
 * "Bilet Listesi" (pages/AirTicketsPage.tsx), since a ticket number is only meaningful
 * once a reservation is actually issued (`status: 'Booked'`), not while still prebooked.
 */
export function AirReservationList({
  status,
  title,
  showTicketColumn,
}: {
  status: BookingStatus;
  title: string;
  showTicketColumn?: boolean;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      startDate: dayjs(),
      endDate: dayjs(),
    },
  });

  const searchMutation = useMutation({
    mutationFn: (values: FilterValues) =>
      getReservationReports({
        insertStartDate: values.startDate.format('YYYY-MM-DD'),
        insertEndDate: values.endDate.format('YYYY-MM-DD'),
        status,
        providerType: 'Air',
      }),
  });

  const rows = searchMutation.data?.reservationReports ?? [];
  const hasSearched = searchMutation.isSuccess || searchMutation.isError;

  const onSubmit = handleSubmit((values) => searchMutation.mutate(values));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={onSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Başlangıç tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.startDate,
                      helperText: errors.startDate?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Bitiş tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.endDate,
                      helperText: errors.endDate?.message,
                    },
                  }}
                />
              )}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={searchMutation.isPending}
              sx={{ mt: { xs: 0, sm: 0.5 } }}
            >
              {searchMutation.isPending ? 'Aranıyor…' : 'Ara'}
            </Button>
          </Stack>
        </Box>
        <ApiErrorAlert error={searchMutation.error} sx={{ mt: 2 }} />
      </Paper>

      {searchMutation.isPending && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {hasSearched && !searchMutation.isError && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PNR</TableCell>
                {showTicketColumn && <TableCell>Bilet No</TableCell>}
                <TableCell>Durum</TableCell>
                <TableCell>Sağlayıcı</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Güzergah</TableCell>
                <TableCell>Toplam Ücret</TableCell>
                <TableCell>Rezervasyon Tarihi</TableCell>
                <TableCell>Son Geçerlilik</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showTicketColumn ? 10 : 9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Bu kriterlere uygun rezervasyon bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={(row.shoppingCartId ?? 'row') + index} hover>
                  <TableCell>
                    <ReservationIdentifierLink row={row} detailBasePath="/air/reservations" />
                  </TableCell>
                  {showTicketColumn && <TableCell>{row.documentNumber || '—'}</TableCell>}
                  <TableCell>{row.statu || '—'}</TableCell>
                  <TableCell>{row.provider || '—'}</TableCell>
                  <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>{[row.origin, row.destination].filter(Boolean).join(' → ') || '—'}</TableCell>
                  <TableCell>{row.totalFare != null ? row.totalFare.toLocaleString('tr-TR') : '—'}</TableCell>
                  <TableCell>{row.bookedAt || row.prebookedAt || '—'}</TableCell>
                  <TableCell>{row.expireAt || '—'}</TableCell>
                  <TableCell>
                    {isPaymentResumable(row) && (
                      <Link component={RouterLink} to={resumePaymentUrl(row)} underline="hover">
                        Ödemeye Git
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
